import {
  ClassTeacherDetailsType,
  RelationshipManagerType,
  SchoolDetailsType,
} from '@lib/types/common'
import { TrialPurchaseIntentValue } from '@lib/utils/trial-intent'
import { createContext, useContext } from 'react'

export type UserProps = {
  loggedIn: boolean
  id: number
  isPaidUser: boolean
  isCompetitionUser: boolean
  username: string
  name: string
  email: string
  phone: string
  voucher?: string
  dob?: string
  gender?: string
  avatar?: string
  hasLaptop?: boolean
  // The address this parent typed that came back undeliverable. Their stored
  // email is a dummy by the time we see this, so it is the only record of
  // which address failed.
  rejectedEmail?: string
  student?: {
    id: number
    grade: string
    parentName?: string
    isVip?: boolean
    stats?: {
      rank: number
      points: number
      pendingAssignmentCount: number
      pendingQuizCount: number
    }
    eligibleCompetitionsCount: number
    relationshipManager?: RelationshipManagerType
    hasCompletedCourseClass?: boolean
    codingClassTeacherDetails?: ClassTeacherDetailsType
    mathClassTeacherDetails?: ClassTeacherDetailsType
    elaClassTeacherDetails?: ClassTeacherDetailsType
    whatsappGroupUrl?: string
    isTrialClassScheduled?: boolean
    autoLoginUrl?: string
    enrolledSubjects?: Array<string>
  }
  teacher?: {
    id: number
    tid: number
    calendarId: number
    educationalQualification: string
    eligibleGrades: {
      grade: string
    }[]
    experience: string
    isVerified: boolean
    hasCompletedClass: boolean
    assignmentsInReviewCount: number
    minSlotStart?: string
    maxSlotStart?: string
    isApolloTeacher?: boolean
    onDemandWindowStart?: string
    onDemandWindowEnd?: string
    weekOffDays?: string[]
    isOnLeave?: boolean
    applicationData?: {
      subjects?: string[]
      gradeRanges?: string[]
      resume?: string
      linkedinUrl?: string
      qualifications?: string[] | string
      previousOrganization?: string[] | string
      systemSpecification?: string
      internetSpeed?: string
      shiftPreferences?: string[]
      hoursPerDay?: string
      weekendAvailable?: boolean
      currentlyEmployed?: boolean
      motivation?: string
      pronunciationScores?: {
        accuracy: number
        fluency: number
        completeness: number
        prosody: number
        overall: number
        cefrLevel: string
        attempts: number
        passageUsed: string
      }
      pronunciationCompleted?: boolean
      grammarScores?: {
        cefrLevel: string
        correctCount: number
        totalCount: number
        accuracyPercentage: number
      }
      grammarCompleted?: boolean
      profileCompleted?: boolean
      profileCompletedAt?: string
      testCompleted?: boolean
      testCompletedAt?: string
      autoproctorEmail?: string
      attemptedAutoproctorTests?: { label: string; testId: string }[]
      autoproctorResults?: {
        testId: string
        label: string
        percentageScored: number
        trustScore: number | null
        testAttemptLabel: string
        testFinishTime: string
        provider: string
      }[]
      // Set by the backend GET /application response once the test is
      // completed - reflects whether the candidate met the hiring criteria.
      isPassed?: boolean
      // Present only when isPassed is false - why the candidate failed, so the
      // review page can show the matching rejection message.
      failureReason?: 'eligibility' | 'scores'
      // Present only when isPassed is true - true if the candidate's only
      // qualifying subject(s) currently have hiring on hold, so the review
      // page shows the on-hold message instead of the plain pass message.
      isOnHold?: boolean
    }
  }
  school: SchoolDetailsType
  leadSource?: {
    id: number
    utmMedium: string
    utmSource: string
    utmCampaign: string
    utmTerm: string
    utmContent: string
  }
  signupReferer?: string
  signupSource?: string
  ethnicity?: string
  languagesSpoken?: string | string[]
  parentStatedTrialPurchaseIntent?: TrialPurchaseIntentValue | null
  // Derived server-side: true while the zero-intent trial-booking cooling-off
  // is still running for this lead (`human.utils.is_trial_booking_shadow_banned`).
  isTrialBookingShadowBanned?: boolean
  // Market zone, derived server-side from the country (`User.zone`), e.g.
  // 'zone_2a'. Null when the country is unset or unmapped.
  zone?: string | null
  // Meta fbc built from the signup fbclid, as the Conversions API sends it.
  // Null when signup carried no ad click or it is past Meta's 90 days.
  metaFbc?: string | null
  preferences?: {
    id: number
    whatsappEnabled: boolean
    canTakeInstantTrialClass: boolean
    classTypePreferences: string[]
  }
  timezone?: string
  isInstantClass?: boolean
  country?: string
  city?: string
  availableLanguagesForCountry?: string[]
  totalCredits: number
  totalCodingCredits: number
  totalMathCredits: number
  totalElaCredits: number
  totalReferralCredits?: number
  displayCredits: boolean
  hasPassword: boolean
  isEligibleForRewind: boolean
  isStaff: boolean
  modLoginUserId?: string
  modLoginUserName?: string
  pendingOnboardings?: {
    id: number
    course: string
    subject: string
    serviceType: string
    totalDiscountedFee: string
    paymentTimestamp: string
    currency: string
    onboardingStage?: string | null
    schedulePreferences?: { day: string; time: string }[]
    specialRequirements?: string
    numOfClassesPerMonth?: number
    hasPreferences?: boolean
  }[]
}
export const SessionContext = createContext<UserProps>({} as UserProps)
export const useSessionContext = () => useContext(SessionContext)
